/**
 * Cliente HTTP mínimo da fundação.
 *
 * Duas regras que valem para todo o frontend do InovaPay 2.0:
 *  1. O frontend NUNCA decide permissão. Esconder botão não é segurança;
 *     a autorização é sempre checada no servidor.
 *  2. Falha de rede/servidor não é convertida em estado vazio ou "0":
 *     ela vira um estado de erro explícito na tela.
 */
import { buscar } from './buscar';

export interface VersionInfo {
  readonly app: string;
  readonly version: string;
  readonly environment: string;
  readonly commit: string | null;
  readonly node: string;
  readonly timezone: string;
}

export interface HealthInfo {
  readonly status: string;
  readonly uptimeSeconds: number;
  readonly timestamp: string;
}

export interface ReadyInfo {
  readonly status: 'ready' | 'not_ready';
  readonly checks: {
    readonly database: {
      readonly status: 'up' | 'down';
      readonly latencyMs?: number | null;
    };
    /**
     * Integridade do schema: migration pendente, checksum divergente ou
     * tabela de controle ausente deixam o sistema não pronto. A API não
     * informa QUAL foi o problema (fica no log dela), só o veredito.
     */
    readonly schema: {
      readonly status: 'ok' | 'not_ready' | 'unknown';
      readonly pendingMigrations: number | null;
    };
  };
}

export type Fetched<T> =
  | { readonly state: 'loading' }
  | { readonly state: 'ok'; readonly data: T; readonly httpStatus: number }
  | { readonly state: 'error'; readonly message: string; readonly httpStatus: number | null };

async function request<T>(path: string): Promise<Fetched<T>> {
  try {
    const response = await buscar(path, { headers: { accept: 'application/json' } });
    const text = await response.text();

    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return {
        state: 'error',
        message: 'Resposta não é JSON válido.',
        httpStatus: response.status,
      };
    }

    // 503 em /ready é uma resposta legítima com corpo: não é erro de rede.
    if (!response.ok && response.status !== 503) {
      return {
        state: 'error',
        message: `HTTP ${String(response.status)}`,
        httpStatus: response.status,
      };
    }

    return { state: 'ok', data: parsed as T, httpStatus: response.status };
  } catch (error) {
    return {
      state: 'error',
      message: error instanceof Error ? error.message : 'Falha de rede.',
      httpStatus: null,
    };
  }
}

export const getVersion = (): Promise<Fetched<VersionInfo>> => request<VersionInfo>('/api/version');
export const getHealth = (): Promise<Fetched<HealthInfo>> => request<HealthInfo>('/health');
export const getReady = (): Promise<Fetched<ReadyInfo>> => request<ReadyInfo>('/ready');
